import {create} from "zustand";
import {RoundTripFlight} from "../models/RoundTripFlight";
import {SharedShortlist, ShortlistError, ShortlistService} from "../services/ShortlistService";

export const SHORTLIST_KEY = "farewise.shortlist.v1";
export const MAX_SAVED_FLIGHTS = 20;

export function itineraryKey(flight: RoundTripFlight): string {
  const outbound = flight.outbound_flights?.length ? flight.outbound_flights : [flight.outbound_flight];
  const inbound = flight.inbound_flights?.length ? flight.inbound_flights : flight.inbound_flight ? [flight.inbound_flight] : [];
  return JSON.stringify([...outbound, ...inbound].map(leg => [leg.departure_iata, leg.destination_iata,
    leg.departure_date, leg.departure_time, leg.arrival_date || "", leg.arrival_time]));
}

interface SavedState {flights: RoundTripFlight[]; sharedId: string | null; expiresAt: number | null}
const empty: SavedState = {flights: [], sharedId: null, expiresAt: null};

export function readShortlist(): SavedState {
  try {
    const value = JSON.parse(localStorage.getItem(SHORTLIST_KEY) || "null");
    if (!value || !Array.isArray(value.flights)) return empty;
    const flights = value.flights.slice(0, MAX_SAVED_FLIGHTS).filter((flight: RoundTripFlight) => {
      try { itineraryKey(flight); return !!flight.outbound_flight && Number.isFinite(flight.total_price); }
      catch { return false; }
    });
    const active = typeof value.sharedId === "string" && /^[A-Za-z0-9_-]{24}$/.test(value.sharedId)
      && value.expiresAt * 1000 > Date.now();
    return {flights, sharedId: active ? value.sharedId : null, expiresAt: active ? value.expiresAt : null};
  } catch { return empty; }
}

interface ShortlistState extends SavedState {
  open: boolean;
  busy: boolean;
  error: string | null;
  setOpen: (open: boolean) => void;
  toggle: (flight: RoundTripFlight) => Promise<void>;
  share: () => Promise<string | undefined>;
  refresh: () => Promise<void>;
  stopSharing: () => Promise<void>;
  acceptShared: (shared: SharedShortlist) => void;
}

function persist(value: SavedState) {
  localStorage.setItem(SHORTLIST_KEY, JSON.stringify(value));
}

function fromShared(shared: SharedShortlist): SavedState {
  return {flights: shared.flights.map(entry => entry.flight), sharedId: shared.id, expiresAt: shared.expires_at};
}

export const useShortlistStore = create<ShortlistState>((set, get) => ({
  ...readShortlist(), open: false, busy: false, error: null,
  setOpen: open => set({open, error: null}),
  acceptShared: shared => {
    if (!shared.can_manage) return;
    const value = fromShared(shared);
    try { persist(value); set({...value, error: null}); }
    catch { set({error: "shortlist.storageError"}); }
  },
  toggle: async flight => {
    if (get().busy) return;
    const state = get();
    const exists = state.flights.some(saved => itineraryKey(saved) === itineraryKey(flight));
    if (!exists && state.flights.length >= MAX_SAVED_FLIGHTS) {
      set({error: "shortlist.limit", open: true}); return;
    }
    const flights = exists ? state.flights.filter(saved => itineraryKey(saved) !== itineraryKey(flight))
      : [...state.flights, flight];
    set({busy: true, error: null});
    try {
      let value: SavedState = {flights, sharedId: state.sharedId, expiresAt: state.expiresAt};
      if (state.sharedId) {
        try { value = fromShared(await ShortlistService.update(state.sharedId, flights)); }
        catch (error) {
          if (!(error instanceof ShortlistError) || error.status !== 404) throw error;
          value = {flights, sharedId: null, expiresAt: null};
        }
      }
      persist({flights: value.flights, sharedId: value.sharedId, expiresAt: value.expiresAt});
      set(value);
    } catch { set({error: "shortlist.saveError", open: true}); }
    finally { set({busy: false}); }
  },
  share: async () => {
    if (get().busy || !get().flights.length) return;
    set({busy: true, error: null});
    try {
      let shared: SharedShortlist;
      const {sharedId, flights} = get();
      if (sharedId) {
        try { shared = await ShortlistService.get(sharedId); }
        catch (error) {
          if (!(error instanceof ShortlistError) || error.status !== 404) throw error;
          shared = await ShortlistService.create(flights);
        }
      } else {
        // Check persistence before publishing, so management access is durable.
        persist({flights, sharedId: null, expiresAt: null});
        shared = await ShortlistService.create(flights);
      }
      const value = fromShared(shared);
      persist(value); set(value);
      return ShortlistService.url(shared.id);
    } catch { set({error: "shortlist.shareError"}); }
    finally { set({busy: false}); }
  },
  refresh: async () => {
    const {sharedId, busy} = get();
    if (!sharedId || busy) return;
    set({busy: true, error: null});
    try { get().acceptShared(await ShortlistService.get(sharedId)); }
    catch (error) {
      if (error instanceof ShortlistError && error.status === 404) {
        const value = {flights: get().flights, sharedId: null, expiresAt: null};
        try { persist(value); set({...value, error: "shortlist.expired"}); }
        catch { set({error: "shortlist.storageError"}); }
      } else set({error: "shortlist.loadError"});
    } finally { set({busy: false}); }
  },
  stopSharing: async () => {
    const {sharedId, busy, flights} = get();
    if (!sharedId || busy) return;
    set({busy: true, error: null});
    try {
      try { await ShortlistService.delete(sharedId); }
      catch (error) { if (!(error instanceof ShortlistError) || error.status !== 404) throw error; }
      const value = {flights, sharedId: null, expiresAt: null};
      persist(value); set(value);
    } catch { set({error: "shortlist.saveError"}); }
    finally { set({busy: false}); }
  },
}));
